import {
  Card,
  Subtitle,
  Title,
  VAlert,
  VButton,
  VDropdownMenu,
  VDropdownMenuContent,
  VDropdownMenuItem,
  VDropdownMenuTrigger,
} from '@vrzl/ui';
import { ChevronDown, Shield } from 'lucide-react';
import { useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router';

import { useAuth } from './authProvider';
import { resolveRedirectTarget } from './redirectTarget';
import { TENANT_CODES, TENANTS, type TenantCode } from './tenants';

const SELECT_REGION_PLACEHOLDER = 'Selecteer uw regio';

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, isLoading, loginWithSSO, sessionExpired } = useAuth();
  // De pagina waarop ProtectedRoute hem tegenhield — een gedeelde documentlink
  // hoort na het inloggen alsnog te openen, niet op de zakenlijst uit te komen.
  const redirectTarget = resolveRedirectTarget(location.state);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  // Geen default: de gebruiker moet bewust een regio kiezen, anders logt hij
  // ongemerkt in op een tenant die hij niet bedoelde.
  const [tenantCode, setTenantCode] = useState<TenantCode | null>(null);

  if (!isLoading && isAuthenticated) {
    return <Navigate to={redirectTarget} replace />;
  }

  async function handleSSO() {
    if (!tenantCode) return;
    setError(null);
    setSubmitting(true);
    try {
      await loginWithSSO(tenantCode);
      navigate(redirectTarget, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Inloggen mislukt');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <Card padding="lg" className="w-full max-w-md">
        <div className="mb-6 flex items-center gap-3">
          <div className="flex size-10 items-center justify-center rounded-md bg-primary-600 text-on-primary">
            <Shield className="size-5" />
          </div>
          <div>
            <Title className="text-xl">SafeZone</Title>
            {tenantCode && <Subtitle>{TENANTS[tenantCode].label}</Subtitle>}
          </div>
        </div>

        {sessionExpired && !error && (
          <div className="mb-4">
            <VAlert variant="info" title="Sessie verlopen">
              Je bent automatisch uitgelogd omdat je sessie is verlopen. Log
              opnieuw in om verder te gaan.
            </VAlert>
          </div>
        )}

        {error && (
          <div className="mb-4">
            <VAlert variant="error" title="Inloggen mislukt">
              {error}
            </VAlert>
          </div>
        )}

        <div className="mb-4">
          <VDropdownMenu>
            <VDropdownMenuTrigger asChild>
              <VButton type="button" variant="outline" fullWidth disabled={submitting}>
                <span className="flex w-full items-center justify-between">
                  <span className={tenantCode ? undefined : 'text-muted-foreground'}>
                    {tenantCode ? TENANTS[tenantCode].label : SELECT_REGION_PLACEHOLDER}
                  </span>
                  <ChevronDown className="size-4" />
                </span>
              </VButton>
            </VDropdownMenuTrigger>
            <VDropdownMenuContent className="w-(--radix-dropdown-menu-trigger-width)">
              {TENANT_CODES.map((code) => (
                <VDropdownMenuItem key={code} onSelect={() => setTenantCode(code)}>
                  {TENANTS[code].label}
                </VDropdownMenuItem>
              ))}
            </VDropdownMenuContent>
          </VDropdownMenu>
        </div>

        <VButton
          type="button"
          variant="primary"
          fullWidth
          onClick={handleSSO}
          disabled={submitting || !tenantCode}
        >
          {submitting ? 'Bezig met inloggen...' : 'Inloggen met Microsoft'}
        </VButton>
      </Card>
    </div>
  );
}
